import { useCallback, useEffect, useRef, useState } from "react";
import { ClockCircleOutlined, FileTextOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Collapse, Empty, Select, Spin, Tag, Typography, message } from "antd";
import dayjs from "dayjs";

import { PermissionKey } from "@fitness-app/data-models";
import {
  traineeAiReportAllowedDays,
  TraineeAiReportAudience,
  TraineeAiReportStatus,
  type TraineeAiReport,
  type TraineeAiReportDays,
} from "@fitness-app/data-models/entities/TraineeAiReport";
import { getErrorMessage } from "@fitness-app/utils";

import { usePermission } from "~/hooks/usePermission";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { traineeKeys, useTraineeId } from "~/modules/Trainee/TraineeProfile/scope";
import { analyticsService, backgroundTasksApi, supabase } from "~/store/initializeStore";
import { ReportMarkdown } from "./ReportMarkdown";
import { TraineeAiReportProgress } from "./TraineeAiReportProgress";
import { TRAINEE_AI_REPORT_CLIENT_AUDIENCE_FEATURE_FLAG } from "./traineeAiReportFeatureFlags";

// Raport AI o klientce, generowany na żądanie z profilu. Wpis powstaje jako
// queued (endpoint w client-web), task trigger.dev przełącza status — stąd
// subskrypcja realtime na trainee_ai_report zamiast pollingu.

interface TraineeAiReportsProps {
  traineeId: string;
}

const audienceLabels: Record<TraineeAiReportAudience, string> = {
  [TraineeAiReportAudience.Trainer]: "Dla trenera",
  [TraineeAiReportAudience.Client]: "Dla klientki",
};

const traineeAiReportsKey = (traineeId: string) => [...traineeKeys.all(traineeId), "aiReports"] as const;

let channelSequence = 0;

const useTraineeAiReports = (traineeId: string, onReportsLoaded: (reports: TraineeAiReport[]) => void) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    channelSequence += 1;
    // Sufiks instancji: ta sama klientka może być otwarta w dwóch kartach, a `supabase.channel(name)`
    // oddaje istniejący kanał, więc zamknięcie jednej karty odpięłoby drugą.
    const channel = supabase
      .channel(`trainee-ai-report-${traineeId}-${channelSequence}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "trainee_ai_report",
          filter: `traineeId=eq.${traineeId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: traineeAiReportsKey(traineeId) });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient, traineeId]);

  return useQuery({
    queryKey: traineeAiReportsKey(traineeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trainee_ai_report")
        .select("*")
        .eq("traineeId", traineeId)
        .order("createdAt", { ascending: false })
        .limit(10)
        .returns<TraineeAiReport[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    },
    onSuccess: onReportsLoaded,
  });
};

const ReportStatusTag = ({ status }: { status: TraineeAiReportStatus }) => {
  if (status === TraineeAiReportStatus.Queued) {
    return (
      <Tag color="processing">
        <Spin size="small" style={{ marginRight: 6 }} />
        Generowanie…
      </Tag>
    );
  }

  if (status === TraineeAiReportStatus.Failed) {
    return <Tag color="error">Niepowodzenie</Tag>;
  }

  return <Tag color="success">Gotowy</Tag>;
};

/** Tab entry for TraineeFeatures — feature tabs take no props and read the
 *  trainee from the card scope, like the other tabs. */
export const TraineeAiReportsTab = () => {
  const traineeId = useTraineeId();

  return <TraineeAiReports traineeId={traineeId} />;
};

export const TraineeAiReports = ({ traineeId }: TraineeAiReportsProps) => {
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);
  // "Dla klientki" stays hidden until the client apps can show the report.
  const { enabled: clientAudienceEnabled } = usePostHogFeatureFlag(TRAINEE_AI_REPORT_CLIENT_AUDIENCE_FEATURE_FLAG);
  const [selectedAudience, setAudience] = useState<TraineeAiReportAudience>(TraineeAiReportAudience.Trainer);
  const audience = clientAudienceEnabled ? selectedAudience : TraineeAiReportAudience.Trainer;
  const [days, setDays] = useState<TraineeAiReportDays>(14);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const seenReportIds = useRef(new Set<string>());

  const openReports = useCallback((reportIds: string[]) => {
    if (!reportIds.length) return;
    setActiveKeys((current) => [...new Set([...current, ...reportIds])]);
  }, []);

  const handleReportsLoaded = useCallback(
    (reports: TraineeAiReport[]) => {
      const isFirstLoad = seenReportIds.current.size === 0;
      const reportIdsToOpen: string[] = [];

      reports.forEach((report, index) => {
        if (seenReportIds.current.has(report.id)) return;
        seenReportIds.current.add(report.id);

        if (
          report.status === TraineeAiReportStatus.Queued ||
          (isFirstLoad && index === 0 && report.status === TraineeAiReportStatus.Completed)
        ) {
          reportIdsToOpen.push(report.id);
        }
      });

      openReports(reportIdsToOpen);
    },
    [openReports],
  );

  const reportsQuery = useTraineeAiReports(traineeId, handleReportsLoaded);

  const generateMutation = useMutation({
    mutationFn: async (): Promise<{ reportId: string }> => {
      const response = await backgroundTasksApi.post<{ reportId: string }>("/api/trainers/ai/generate-trainee-report", {
        traineeId,
        audience,
        days,
      });
      return response.data;
    },
    onSuccess: ({ reportId }) => {
      // TRE-2057 — after the endpoint accepted the run, not on click: a 403/409
      // must not count as a requested report.
      analyticsService.track("trainee_ai_report_requested", {
        traineeId,
        reportId,
        audience,
        days,
        source: "trainee_profile",
      });
      openReports([reportId]);
      void reportsQuery.refetch();
      void message.success("Generowanie raportu uruchomione - wynik pojawi się poniżej.");
    },
    onError: (error) => {
      void message.error(`Nie udało się uruchomić raportu: ${getErrorMessage(error)}`);
    },
  });

  if (!canUseAgents) {
    return null;
  }

  const reports = reportsQuery.data ?? [];
  const hasQueuedReport = reports.some((report) => report.status === TraineeAiReportStatus.Queued);

  return (
    <Card
      title={
        <span>
          <FileTextOutlined style={{ marginRight: 8 }} />
          Raport AI
        </span>
      }
      extra={
        <div className="flex items-center gap-2">
          {clientAudienceEnabled ? (
            <Select<TraineeAiReportAudience>
              size="small"
              style={{ width: 130 }}
              value={audience}
              onChange={setAudience}
              options={Object.entries(audienceLabels).map(([value, label]) => ({ value, label }))}
            />
          ) : null}
          <Select<TraineeAiReportDays>
            size="small"
            style={{ width: 110 }}
            value={days}
            onChange={setDays}
            options={traineeAiReportAllowedDays.map((value) => ({ value, label: `${value} dni` }))}
          />
          <Button
            type="primary"
            size="small"
            loading={generateMutation.isLoading}
            disabled={hasQueuedReport}
            onClick={() => generateMutation.mutate()}
          >
            {hasQueuedReport ? "Generowanie…" : "Generuj"}
          </Button>
        </div>
      }
    >
      {reportsQuery.isLoading ? (
        <Spin />
      ) : reportsQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Nie udało się pobrać raportów AI"
          description={getErrorMessage(reportsQuery.error)}
        />
      ) : reports.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Brak raportów - wygeneruj pierwszy raport na podstawie aktywności, notatek i wiadomości klientki."
        />
      ) : (
        <Collapse
          activeKey={activeKeys}
          onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
          items={reports.map((report) => ({
            key: report.id,
            label: (
              <div className="flex items-center gap-2">
                <Typography.Text type="secondary" className="text-sm">
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  {dayjs(report.createdAt).format("DD.MM.YYYY HH:mm")}
                </Typography.Text>
                <Tag>{audienceLabels[report.audience] ?? report.audience}</Tag>
                <Tag>{report.days} dni</Tag>
                <ReportStatusTag status={report.status} />
              </div>
            ),
            children:
              report.status === TraineeAiReportStatus.Failed ? (
                <Alert
                  type="error"
                  showIcon
                  message="Generowanie nie powiodło się"
                  description={report.metadata?.error ?? "Spróbuj ponownie - kliknij Generuj."}
                />
              ) : report.status === TraineeAiReportStatus.Queued ? (
                <TraineeAiReportProgress runId={report.metadata?.runId} accessToken={report.metadata?.accessToken} />
              ) : (
                <div className="max-w-none">
                  <ReportMarkdown>{report.content ?? ""}</ReportMarkdown>
                </div>
              ),
          }))}
        />
      )}
    </Card>
  );
};
